import { trackProductAction } from '@/libs/product-analytics';
import api from '@/libs/axiosInstance';
import type { EmailMetadata } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface EmailOAuthRedirectResponse {
  redirectUrl: string;
}

interface GmailSenderResponse {
  senderEmail: string;
  metadata: EmailMetadata;
}

/**
 * Asks the backend for the provider's OAuth URL. `returnTo` is the in-app path the
 * backend sends the brand back to once connected; without it the backend uses its default.
 */
const fetchRedirectUrl = async (
  provider: 'gmail' | 'outlook',
  returnTo?: string
): Promise<EmailOAuthRedirectResponse> => {
  const url = `/dashboard/integrations/${provider}/redirect-url`;
  const response = returnTo ? await api.get(url, { params: { returnTo } }) : await api.get(url);
  return response.data.data;
};

// Gmail hooks

export const useGmailRedirectUrl = (returnTo?: string) => {
  return useMutation({
    mutationFn: () =>
      trackProductAction('integrations', 'connect', () => fetchRedirectUrl('gmail', returnTo)),
  });
};

export const useGmailDisconnect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      return trackProductAction('integrations', 'disconnect', async () => {
        await api.post('/dashboard/integrations/gmail/disconnect');
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
};

export const useUpdateGmailSender = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (senderEmail: string): Promise<GmailSenderResponse> => {
      return trackProductAction('integrations', 'update', async () => {
        const response = await api.post('/dashboard/integrations/gmail/sender', { senderEmail });
        return response.data.data;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
};

// Outlook hooks

export const useOutlookRedirectUrl = (returnTo?: string) => {
  return useMutation({
    mutationFn: () =>
      trackProductAction('integrations', 'connect', () => fetchRedirectUrl('outlook', returnTo)),
  });
};

export const useOutlookDisconnect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      return trackProductAction('integrations', 'disconnect', async () => {
        await api.post('/dashboard/integrations/outlook/disconnect');
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
};
